import {
  deriveCapabilities,
  getModelMetadata,
  type LiveModelLike,
  type ModelCapability,
  type ModelStrength,
} from "@workspace/personas";
import { Brain, Eye, Sparkles, Wrench, Zap } from "lucide-react";

import type { Model } from "../../types";

/**
 * Two-slot speed↔smart tradeoff indicator for the selector (tdd-0044). Sets an
 * expectation rather than a precise score: is this a fast model for rapid
 * iteration, or a slow one you'd hand a detailed prompt and leave running?
 *
 *   ⚡⚡  pure speed   — fast enough to iterate on quickly
 *   ⚡🧠  balanced
 *   🧠🧠  pure smart   — write a deep prompt, leave it to run
 *
 * Derived from the speed/smart balance: a clear lead either way fills both slots
 * with that side's icon; a near-tie shows one of each. The builder keeps the
 * full-resolution per-axis meters.
 */
export function SpeedSmartBalance({ model }: { model: Model }) {
  const meta = getModelMetadata(model.id);
  const lead = meta.speed - meta.smart; // >0 faster, <0 smarter

  // Two slots, left→right. A clear lead (≥2) fills both with that icon; a
  // near-tie shows one of each (speed on the left).
  let slots: ("speed" | "smart")[];
  let label: string;
  if (lead >= 2) {
    slots = ["speed", "speed"];
    label = "Fast — good for rapid iteration";
  } else if (lead <= -2) {
    slots = ["smart", "smart"];
    label = "Capable — best with a detailed prompt, left to run";
  } else {
    slots = ["speed", "smart"];
    label = "Balanced speed and capability";
  }

  return (
    <span
      className="inline-flex items-center gap-0.5"
      title={`${label} (speed ${meta.speed}/5 · smart ${meta.smart}/5)`}
      aria-label={label}
    >
      {slots.map((s, i) =>
        s === "speed" ? (
          <Zap key={i} className="h-3.5 w-3.5 text-yellow-500" />
        ) : (
          <Brain key={i} className="h-3.5 w-3.5 text-blue-500" />
        ),
      )}
    </span>
  );
}

/** A small filled/empty pip meter for a 1–5 guidance score. */
function ScoreMeter({ score, label }: { score: number; label: string }) {
  return (
    <span className="inline-flex items-center gap-1" title={`${label}: ${score}/5`}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="inline-flex gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            className={
              "h-1.5 w-1.5 rounded-full " +
              (n <= score ? "bg-primary" : "bg-muted-foreground/25")
            }
          />
        ))}
      </span>
    </span>
  );
}

const CAPABILITY_META: Record<
  ModelCapability,
  { icon: typeof Wrench; label: string }
> = {
  tools: { icon: Wrench, label: "Tools" },
  reasoning: { icon: Brain, label: "Reasoning" },
  vision: { icon: Eye, label: "Vision" },
};

const STRENGTH_LABEL: Record<ModelStrength, string> = {
  technical: "Technical",
  multilingual: "Multilingual",
  "extended-context": "Long context",
  creative: "Creative",
};

/**
 * Guidance badges for a model (tdd-0044): speed + smart meters, derived
 * capability icons (tools/reasoning/vision), and curated strength chips. Used in
 * the persona builder's model grid and the bare-model rows of the selector.
 */
/** Derived capability chips (tools/reasoning/vision). Labels hidden when compact. */
function CapabilityChips({
  model,
  compact = false,
}: {
  model: Model;
  compact?: boolean;
}) {
  const capabilities = deriveCapabilities(model as LiveModelLike);
  return (
    <>
      {capabilities.map((cap) => {
        const { icon: Icon, label } = CAPABILITY_META[cap];
        return (
          <span
            key={cap}
            className="inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[10px] text-accent-foreground"
          >
            <Icon className="h-3 w-3" />
            {!compact && label}
          </span>
        );
      })}
    </>
  );
}

/**
 * Compact badges for the SELECTOR rows (tdd-0044): the speed↔smart balance bar
 * plus capability icons (no labels, no strengths). Glanceable; full fidelity is
 * reserved for the builder via {@link ModelBadges}.
 */
export function SelectorModelBadges({ model }: { model: Model }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SpeedSmartBalance model={model} />
      <CapabilityChips model={model} compact />
    </div>
  );
}

/**
 * Full-resolution badges for the BUILDER model grid: independent speed + smart
 * meters (both axes visible), capability chips, and strength chips.
 */
export function ModelBadges({ model }: { model: Model }) {
  const meta = getModelMetadata(model.id);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-3">
        <ScoreMeter score={meta.speed} label="Speed" />
        <ScoreMeter score={meta.smart} label="Smart" />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <CapabilityChips model={model} />
        {meta.strengths.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground"
          >
            <Sparkles className="h-2.5 w-2.5" />
            {STRENGTH_LABEL[s]}
          </span>
        ))}
      </div>
    </div>
  );
}
